/**
 * String events and tallies on the hand's tour sheet (fieldiq-api#1286, WS-3),
 * through the six /field routes. SNAKE_CASE reads, as the API sends them.
 *
 * WHILE THE SHEET IS A DRAFT the hand adds events and tallies, removes an event
 * they recorded (not a correction), and edits or deletes a tally. ONCE IT IS
 * SUBMITTED this section is read only: the field doors refuse
 * (tour_submitted / tour_approved), so nothing to write with is offered, and
 * the screen says the office makes corrections from here on.
 *
 * Tally totals are the API's; nothing here adds the joints up.
 */
import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  useAddWsFieldStringEvent,
  useCreateWsFieldTally,
  useRemoveWsFieldStringEvent,
  useRemoveWsFieldTally,
  useSetWsFieldTallyLines,
  useWsFieldTourString,
} from '../../../hooks/useWsString';
import {
  emptyWsEventForm,
  fmtWsNumber,
  wsDepthRange,
  wsEntered,
  wsEventBody,
  wsItemIdentity,
  wsJobRefusal,
  wsTallyLinesBody,
  type WsEventForm,
  type WsTallyLineForm,
} from '../../../lib/well-servicing';
import type { WsCatalog, WsFieldStringEvent, WsFieldTally } from '../../../types/well-servicing';
import { PT_FIELD_SECONDARY, PtFieldCard, PtFieldQueryState } from '../../pressure-testing/field/PtFieldBits';
import { StringEventForm, TallyLinesEditor, TallyTotals } from '../StringSections';
import { WS_INPUT, WS_PRIMARY, WS_SECONDARY, WsField, WsFormError } from '../WsBits';

/** The field tally's totals in the office component's shape (the values are the API's, unchanged). */
const officeTotals = (t: WsFieldTally) => ({
  lengthUnit: t.length_unit,
  totals: {
    joints: t.totals.joints,
    totalLength: t.totals.total_length,
    totalLengthM: t.totals.total_length_m,
    averageLength: t.totals.average_length,
    shortest: t.totals.shortest,
    longest: t.totals.longest,
    byCondition: t.totals.by_condition,
  },
});

/** The hand may change the string only on a draft sheet they can write. */
export const wsFieldStringEditable = (tourStatus: string | null | undefined, canWrite: boolean): boolean => tourStatus === 'draft' && canWrite;

const blankLine = (): WsTallyLineForm => ({ length: '', serialNumber: '', condition: '' });

function FieldTally({ tally, editable, catalog }: { tally: WsFieldTally; editable: boolean; catalog: WsCatalog | null }) {
  const setLines = useSetWsFieldTallyLines();
  const remove = useRemoveWsFieldTally();
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState<WsTallyLineForm[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canChange = editable && !tally.locked;
  return (
    <PtFieldCard className="space-y-3 px-4 py-3">
      <div data-testid="ws-field-tally" data-locked={tally.locked ? 'true' : 'false'} className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className={`text-sm font-extrabold ${tally.voided ? 'text-fieldiq-muted line-through' : 'text-white'}`}>
            {tally.tally_kind_label ?? tally.tally_kind} · {tally.direction_label ?? tally.direction}
          </p>
          <p className="text-xs text-fieldiq-text">
            {wsItemIdentity({ description: tally.description, size: tally.size, grade: tally.grade })} · in {tally.length_unit}
          </p>
          {tally.voided && <p className="text-xs text-fi-red">Voided: {tally.void_reason || '—'}</p>}
        </div>
        {canChange && !editing && (
          <div className="flex gap-2">
            <button
              type="button"
              className={WS_SECONDARY}
              aria-label="Edit joints"
              onClick={() => {
                setError(null);
                setRows(tally.lines.length ? tally.lines.map((l) => ({ length: String(l.length_entered), serialNumber: l.serial_number ?? '', condition: l.condition ?? '' })) : [blankLine()]);
                setEditing(true);
              }}
            >
              <Pencil className="size-4" aria-hidden="true" />
              Joints
            </button>
            <button type="button" className={WS_SECONDARY} aria-label="Delete tally" onClick={() => { setError(null); setConfirmDelete(true); }}>
              <Trash2 className="size-4" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      <TallyTotals tally={officeTotals(tally)} conditions={catalog?.stringConditions} />
      <WsFormError message={error} />
      {confirmDelete && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-fi-control border border-fieldiq-border px-3 py-2">
          <p className="text-sm text-fieldiq-text">Delete this tally and its joints?</p>
          <div className="flex gap-2">
            <button type="button" className={WS_SECONDARY} onClick={() => setConfirmDelete(false)} disabled={remove.isPending}>
              Keep
            </button>
            <button
              type="button"
              className={WS_PRIMARY}
              disabled={remove.isPending}
              onClick={() =>
                remove.mutate(tally.id, {
                  onSuccess: () => toast.success('Tally deleted'),
                  onError: (err) => {
                    setConfirmDelete(false);
                    setError(wsJobRefusal(err, 'The tally could not be deleted.'));
                  },
                })
              }
            >
              Delete
            </button>
          </div>
        </div>
      )}
      {editing ? (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            const built = wsTallyLinesBody(rows);
            if ('error' in built) return setError(built.error);
            setLines.mutate(
              { id: tally.id, lines: built.lines },
              {
                onSuccess: () => {
                  toast.success('Joints saved');
                  setEditing(false);
                },
                onError: (err) => setError(wsJobRefusal(err, 'The joints could not be saved.')),
              },
            );
          }}
        >
          <TallyLinesEditor rows={rows} onChange={setRows} catalog={catalog} unit={tally.length_unit} />
          <div className="flex justify-end gap-2">
            <button type="button" className={WS_SECONDARY} onClick={() => setEditing(false)} disabled={setLines.isPending}>
              Cancel
            </button>
            <button type="submit" className={WS_PRIMARY} disabled={setLines.isPending}>
              {setLines.isPending ? 'Saving…' : 'Save joints'}
            </button>
          </div>
        </form>
      ) : tally.lines.length === 0 ? (
        <p className="text-sm text-fieldiq-muted">No joints yet.</p>
      ) : (
        <ol className="space-y-0.5 text-sm text-fieldiq-text" data-testid="ws-field-tally-lines">
          {tally.lines.map((l) => (
            <li key={l.id} className="font-mono">
              {l.line_no}. {fmtWsNumber(l.length_entered)} {tally.length_unit}
              {l.serial_number ? ` · ${l.serial_number}` : ''}
              {l.condition_label ? ` · ${l.condition_label}` : ''}
            </li>
          ))}
        </ol>
      )}
      </div>
    </PtFieldCard>
  );
}

export function WsFieldStringSection({
  tourId, tourStatus, canWrite, catalog, depthUnit,
}: {
  tourId: string;
  tourStatus: string;
  canWrite: boolean;
  catalog: WsCatalog | null;
  depthUnit: string;
}) {
  const query = useWsFieldTourString(tourId);
  const add = useAddWsFieldStringEvent();
  const remove = useRemoveWsFieldStringEvent();
  const createTally = useCreateWsFieldTally();
  const editable = wsFieldStringEditable(tourStatus, canWrite);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [tallying, setTallying] = useState(false);
  const [tallyError, setTallyError] = useState<string | null>(null);
  const [tally, setTally] = useState({ kind: '', direction: '', size: '', grade: '', unit: depthUnit });
  const [rows, setRows] = useState<WsTallyLineForm[]>([blankLine()]);
  const depthUnits = catalog?.depthUnits?.length ? catalog.depthUnits : ['m', 'ft'];
  const data = query.data?.data ?? null;
  const events: WsFieldStringEvent[] = data?.events ?? [];
  const tallies: WsFieldTally[] = data?.tallies ?? [];

  const submitEvent = (f: WsEventForm) => {
    setError(null);
    const built = wsEventBody(f, 'draft', { field: true });
    if ('error' in built) return setError(built.error);
    add.mutate(
      { tourId, body: built.body },
      {
        onSuccess: () => {
          toast.success('String event added');
          setAdding(false);
        },
        onError: (err) => setError(wsJobRefusal(err, 'The event could not be added.')),
      },
    );
  };

  return (
    <section className="space-y-3" data-testid="ws-field-string" data-editable={editable ? 'true' : 'false'}>
      <h2 className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">String</h2>
      {!editable && tourStatus !== 'draft' && (
        <p className="text-sm text-fieldiq-muted" data-testid="ws-field-string-locked">
          This sheet is submitted, so its string events and tallies are read only. Ask the office to make a correction.
        </p>
      )}
      <PtFieldQueryState query={query} subject="the string on this sheet">
        <>
          <WsFormError message={rowError} />
          {events.length === 0 ? (
            <p className="text-sm text-fieldiq-muted">No string event on this sheet.</p>
          ) : (
            <ul className="space-y-2">
              {events.map((e) => (
                <li key={e.id} data-testid="ws-field-string-event">
                  <PtFieldCard className="flex items-start justify-between gap-2 px-4 py-3">
                    <div className="min-w-0">
                      <p className={`text-sm font-extrabold ${e.voided ? 'text-fieldiq-muted line-through' : 'text-white'}`}>
                        {e.seq}. {e.action_label ?? e.action} · {e.component_kind_label ?? e.component_kind} × {fmtWsNumber(e.quantity)}
                      </p>
                      <p className="text-xs text-fieldiq-text">
                        {wsItemIdentity({ description: e.description, size: e.size, grade: e.grade, serialNumber: e.serial_number })} · Length{' '}
                        {wsEntered(e.length)} · Depth {wsDepthRange(e.top_depth, e.bottom_depth)}
                        {e.condition_label ? ` · ${e.condition_label}` : ''}
                      </p>
                      {e.voided && <p className="text-xs text-fi-red">Voided: {e.void_reason || '—'}</p>}
                      {e.corrects_event_id && <p className="text-xs text-fi-amber">Correction by the office</p>}
                    </div>
                    {editable && !e.voided && !e.corrects_event_id && (
                      <button
                        type="button"
                        className={WS_SECONDARY}
                        aria-label={`Remove event ${e.seq}`}
                        disabled={remove.isPending}
                        onClick={() => {
                          setRowError(null);
                          remove.mutate(e.id, {
                            onSuccess: () => toast.success('Event removed'),
                            onError: (err) => setRowError(wsJobRefusal(err, 'The event could not be removed.')),
                          });
                        }}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </button>
                    )}
                  </PtFieldCard>
                </li>
              ))}
            </ul>
          )}
          {editable &&
            (adding ? (
              <PtFieldCard className="px-4 py-3">
                <StringEventForm
                  catalog={catalog}
                  depthUnits={depthUnits}
                  initial={emptyWsEventForm(depthUnit)}
                  mode="draft"
                  pending={add.isPending}
                  error={error}
                  submitLabel="Add event"
                  onCancel={() => setAdding(false)}
                  onSubmit={submitEvent}
                />
              </PtFieldCard>
            ) : (
              <button type="button" className={`${PT_FIELD_SECONDARY} w-full`} onClick={() => { setError(null); setAdding(true); }} data-testid="ws-field-add-event">
                <Plus className="size-4" aria-hidden="true" />
                Add string event
              </button>
            ))}

          <h2 className="pt-2 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Tallies</h2>
          {tallies.length === 0 ? <p className="text-sm text-fieldiq-muted">No tally on this sheet.</p> : tallies.map((t) => <FieldTally key={t.id} tally={t} editable={editable} catalog={catalog} />)}
          {editable &&
            (tallying ? (
              <PtFieldCard className="space-y-3 px-4 py-3">
                <form
                  className="space-y-3"
                  data-testid="ws-field-tally-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    setTallyError(null);
                    if (!tally.kind || !tally.direction) return setTallyError('Choose the kind and the direction.');
                    const built = wsTallyLinesBody(rows);
                    if ('error' in built) return setTallyError(built.error);
                    createTally.mutate(
                      {
                        tourId,
                        body: {
                          tally_kind: tally.kind, direction: tally.direction, size: tally.size.trim() || null, grade: tally.grade.trim() || null,
                          length_unit: tally.unit, lines: built.lines,
                        },
                      },
                      {
                        onSuccess: () => {
                          toast.success('Tally added');
                          setTallying(false);
                        },
                        onError: (err) => setTallyError(wsJobRefusal(err, 'The tally could not be added.')),
                      },
                    );
                  }}
                >
                  <WsFormError message={tallyError} />
                  <div className="grid grid-cols-2 gap-3">
                    <WsField label="Kind">
                      <select className={WS_INPUT} aria-label="Tally kind" value={tally.kind} onChange={(e) => setTally({ ...tally, kind: e.target.value })}>
                        <option value="">Choose…</option>
                        {(catalog?.tallyKinds ?? []).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </WsField>
                    <WsField label="Direction">
                      <select className={WS_INPUT} aria-label="Tally direction" value={tally.direction} onChange={(e) => setTally({ ...tally, direction: e.target.value })}>
                        <option value="">Choose…</option>
                        {(catalog?.tallyDirections ?? []).map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </WsField>
                    <WsField label="Size">
                      <input className={WS_INPUT} aria-label="Tally size" value={tally.size} onChange={(e) => setTally({ ...tally, size: e.target.value })} />
                    </WsField>
                    <WsField label="Grade">
                      <input className={WS_INPUT} aria-label="Tally grade" value={tally.grade} onChange={(e) => setTally({ ...tally, grade: e.target.value })} />
                    </WsField>
                    <WsField label="Unit">
                      <select className={WS_INPUT} aria-label="Tally length unit" value={tally.unit} onChange={(e) => setTally({ ...tally, unit: e.target.value })}>
                        {depthUnits.map((u) => (
                          <option key={u} value={u}>
                            {u}
                          </option>
                        ))}
                      </select>
                    </WsField>
                  </div>
                  <TallyLinesEditor rows={rows} onChange={setRows} catalog={catalog} unit={tally.unit} />
                  <div className="flex justify-end gap-2">
                    <button type="button" className={WS_SECONDARY} onClick={() => setTallying(false)} disabled={createTally.isPending}>
                      Cancel
                    </button>
                    <button type="submit" className={WS_PRIMARY} disabled={createTally.isPending}>
                      {createTally.isPending ? 'Saving…' : 'Add tally'}
                    </button>
                  </div>
                </form>
              </PtFieldCard>
            ) : (
              <button
                type="button"
                className={`${PT_FIELD_SECONDARY} w-full`}
                data-testid="ws-field-new-tally"
                onClick={() => {
                  setTallyError(null);
                  setTally({ kind: '', direction: '', size: '', grade: '', unit: depthUnit });
                  setRows([blankLine()]);
                  setTallying(true);
                }}
              >
                <Plus className="size-4" aria-hidden="true" />
                New tally
              </button>
            ))}
        </>
      </PtFieldQueryState>
    </section>
  );
}
